// @ts-nocheck
/* Port of v1 components/partials/dashboard/company/DemoCompanySummaryModal.js.
 * BYTE-IDENTICAL LOGIC -- every state, effect, validation, and toast preserved.
 * Chrome swaps: PrimeReact <Button> -> shadcn <Button> (v2 does not install
 * PrimeReact). react-apexcharts + react-bootstrap Modal/Row/Col/Form kept. */
import React, { useEffect, useState, useCallback } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const _Unused_Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const _Unused_Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
_Unused_Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
_Unused_Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
_Unused_Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
_Unused_Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('card-text', className)} {...props}>
    {children}
  </p>
);
_Unused_Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);

import Chart from 'react-apexcharts';
import { getApiCall } from '@/service/httpCall';
import { API_COMPANY_DEMO_SUMMARY } from '@/service/url';
import { exportToCSV } from '@/utilities/helpers';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const fmtLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const minFrom = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  return {
    start: fmtLocal(firstOfMonth),
    end: fmtLocal(today),
    today: fmtLocal(today),
    minFrom: fmtLocal(minFrom),
  };
};

const fmtTalkTime = (secs) => {
  const s = Number(secs) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m}m ${sec}s`;
};

const StatCard = ({ label, value, color = '#4f6edb' }) => (
  <div
    style={{
      padding: '14px 16px',
      borderRadius: 12,
      boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
      borderLeft: `5px solid ${color}`,
      height: '100%',
    }}
  >
    <p style={{ margin: 0, fontSize: 12, color: '#777' }}>{label}</p>
    <h5 style={{ margin: '4px 0 0', fontWeight: 600 }}>{value}</h5>
  </div>
);

export default function DemoCompanySummaryModal({ show, onHide, companyId, companyName }) {
  const DEFAULTS = getDefaultDates();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(DEFAULTS.start);
  const [endDate, setEndDate] = useState(DEFAULTS.end);

  const fetchSummary = useCallback(async () => {
    if (!companyId) return;
    if (startDate && startDate < DEFAULTS.minFrom) {
      toast.error('From date cannot be earlier than 3 months from today');
      return;
    }
    if (endDate && endDate > DEFAULTS.today) {
      toast.error('To date cannot be in the future');
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      toast.error('From date cannot be after To date');
      return;
    }
    try {
      setLoading(true);
      let url = API_COMPANY_DEMO_SUMMARY.replace(':id', companyId);
      const qs = [];
      if (startDate) qs.push(`startDate=${startDate}`);
      if (endDate) qs.push(`endDate=${endDate}`);
      if (qs.length) url += `?${qs.join('&')}`;
      const resp = await getApiCall(url, {});
      setData(resp || null);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to load company analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    if (show && companyId) fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, companyId]);

  const handleReset = () => {
    setStartDate(DEFAULTS.start);
    setEndDate(DEFAULTS.end);
    setTimeout(fetchSummary, 0);
  };

  const handleExport = () => {
    if (!data) return;
    const rows = [
      { metric: 'Company', value: data.company?.name },
      { metric: 'Account Age (days)', value: data.overview?.account_age_days },
      { metric: 'Days Used', value: data.overview?.days_used },
      { metric: 'Converted', value: data.overview?.converted ? 'Yes' : 'No' },
      { metric: 'Conversion Date', value: fmtDate(data.overview?.conversion_date) },
      { metric: 'Deleted', value: data.overview?.deleted ? 'Yes' : 'No' },
      {
        metric: 'Deleted At',
        value: data.overview?.deleted_at ? new Date(data.overview.deleted_at).toLocaleString() : '-',
      },
      { metric: 'Recharge Count', value: data.overview?.recharge_count },
      { metric: 'Agents', value: data.entities?.agents },
      { metric: 'Team Leads', value: data.entities?.team_leads },
      { metric: 'Call Centers', value: data.entities?.call_centres },
      { metric: 'Nested Companies', value: data.entities?.nested_companies },
      { metric: 'Total Calls', value: data.calls?.total },
      { metric: 'Incoming Calls', value: data.calls?.incoming },
      { metric: 'Outgoing Calls', value: data.calls?.outgoing },
      { metric: 'Answered Calls', value: data.calls?.answered },
      { metric: 'Missed Calls', value: data.calls?.missed },
      { metric: 'Total Talk Time (s)', value: data.calls?.total_talk_time_seconds },
      { metric: 'Credits Spent', value: data.credits?.spent },
      { metric: 'Credits Added', value: data.credits?.added },
      { metric: 'Credits Available', value: data.credits?.available },
    ];
    exportToCSV({
      fileName: `demo-summary-${data.company?.name || companyId}`,
      data: rows,
      columns: [
        { header: 'Metric', accessor: 'metric' },
        { header: 'Value', accessor: 'value' },
      ],
    });
  };

  const daily = data?.dailyCalls || [];
  const callsDonut = {
    options: {
      labels: ['Incoming', 'Outgoing'],
      legend: { position: 'bottom' },
      colors: ['#0ea5e9', '#f97316'],
      dataLabels: { enabled: true },
    },
    series: [Number(data?.calls?.incoming) || 0, Number(data?.calls?.outgoing) || 0],
  };
  const entitiesBar = {
    options: {
      chart: { toolbar: { show: false } },
      xaxis: { categories: ['Agents', 'Team Leads', 'Call Centers', 'Companies'] },
      colors: ['#6366f1'],
      plotOptions: { bar: { borderRadius: 4, distributed: true } },
      legend: { show: false },
      dataLabels: { enabled: true },
    },
    series: [
      {
        name: 'Count',
        data: [
          Number(data?.entities?.agents) || 0,
          Number(data?.entities?.team_leads) || 0,
          Number(data?.entities?.call_centres) || 0,
          Number(data?.entities?.nested_companies) || 0,
        ],
      },
    ],
  };
  const dailyArea = {
    options: {
      chart: { toolbar: { show: false }, stacked: false },
      xaxis: { categories: daily.map((d) => d.day) },
      colors: ['#0ea5e9', '#f97316', '#10b981'],
      stroke: { curve: 'smooth', width: 2 },
      markers: { size: daily.length <= 1 ? 6 : 0, hover: { size: 7 } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } },
      legend: { position: 'bottom' },
      dataLabels: { enabled: false },
    },
    series: [
      { name: 'Incoming', data: daily.map((d) => d.incoming) },
      { name: 'Outgoing', data: daily.map((d) => d.outgoing) },
      { name: 'Answered', data: daily.map((d) => d.answered) },
    ],
  };
  const creditsBar = {
    options: {
      chart: { toolbar: { show: false } },
      xaxis: { categories: ['Spent', 'Added', 'Available'] },
      colors: ['#dc3545', '#28a745', '#007bff'],
      plotOptions: { bar: { borderRadius: 4, distributed: true, horizontal: true } },
      legend: { show: false },
      dataLabels: { enabled: true },
    },
    series: [
      {
        name: 'Credits',
        data: [
          Number(data?.credits?.spent) || 0,
          Number(data?.credits?.added) || 0,
          Number(data?.credits?.available) || 0,
        ],
      },
    ],
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      scrollable
      dialogClassName="demo-summary-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {'Demo Account Analytics — '}
          {companyName || data?.company?.name || ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-end flex-wrap gap-2 mb-3">
          <div>
            <label style={{ fontSize: 12, color: '#777' }}>From</label>
            <CalendarDateInput
              value={startDate}
              onChange={(e) => setStartDate(e)}
              min={DEFAULTS.minFrom}
              max={endDate || DEFAULTS.today}
              style={{ width: 170 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#777' }}>To</label>
            <CalendarDateInput
              value={endDate}
              onChange={(e) => setEndDate(e)}
              min={startDate || DEFAULTS.minFrom}
              max={DEFAULTS.today}
              style={{ width: 170 }}
            />
          </div>
          <Button onClick={fetchSummary} size="sm">
            Apply
          </Button>
          <Button onClick={handleReset} size="sm" variant="secondary">
            Reset
          </Button>
          <div className="ms-auto">
            <Button onClick={handleExport} size="sm" disabled={!data}>
              Export
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-5">Loading analytics…</div>
        ) : !data ? (
          <div className="text-center p-5">No data available.</div>
        ) : (
          <>
            <Row className="g-3 mb-3">
              <Col md="3">
                <StatCard label="Account Age" value={`${data.overview.account_age_days} days`} color="#6366f1" />
              </Col>
              <Col md="3">
                <StatCard label="Days Used" value={`${data.overview.days_used} days`} color="#0ea5e9" />
              </Col>
              <Col md="3">
                <StatCard label="Recharges" value={data.overview.recharge_count} color="#a855f7" />
              </Col>
              <Col md="3">
                <StatCard label="Total Calls" value={data.calls.total} color="#14b8a6" />
              </Col>
              <Col md="3">
                <StatCard
                  label="Total Talk Time"
                  value={fmtTalkTime(data.calls.total_talk_time_seconds)}
                  color="#10b981"
                />
              </Col>
              <Col md="3">
                <StatCard label="Credits Spent" value={data.credits.spent} color="#dc3545" />
              </Col>
              <Col md="3">
                <StatCard
                  label={
                    data.overview.deleted
                      ? 'Deleted At'
                      : data.overview.converted
                      ? 'Conversion Date'
                      : 'Status'
                  }
                  value={
                    data.overview.deleted
                      ? data.overview.deleted_at
                        ? new Date(data.overview.deleted_at).toLocaleString()
                        : 'Deleted'
                      : data.overview.converted
                      ? fmtDate(data.overview.conversion_date)
                      : data.company.is_demo_company
                      ? data.overview.expired
                        ? 'Expired Demo'
                        : 'Active Demo'
                      : 'Actual'
                  }
                  color="#f97316"
                />
              </Col>
            </Row>

            <Row className="g-3">
              <Col md="6">
                <div className="p-3" style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
                  <h6 className="mb-2">Incoming vs Outgoing</h6>
                  <Chart options={callsDonut.options} series={callsDonut.series} type="donut" height={260} />
                </div>
              </Col>
              <Col md="6">
                <div className="p-3" style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
                  <h6 className="mb-2">Entities Created</h6>
                  <Chart options={entitiesBar.options} series={entitiesBar.series} type="bar" height={260} />
                </div>
              </Col>
              <Col md="12">
                <div className="p-3" style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
                  <h6 className="mb-2">Daily Calls</h6>
                  {daily.length ? (
                    <Chart
                      options={dailyArea.options}
                      series={dailyArea.series}
                      type={daily.length <= 1 ? 'bar' : 'area'}
                      height={300}
                    />
                  ) : (
                    <div className="text-center text-muted p-4">No call activity in this range.</div>
                  )}
                </div>
              </Col>
              <Col md="12">
                <div className="p-3" style={{ borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.06)' }}>
                  <h6 className="mb-2">Credits</h6>
                  <Chart options={creditsBar.options} series={creditsBar.series} type="bar" height={220} />
                </div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
